/**
 * The panel profile fields a check can target beyond age, gender, state, DMA
 * and region. Each key is the field the panel count reads (its quota type), and
 * the values are that field's own answer labels, listed by the panel count's
 * options. Values within a field are OR'd; fields are AND'd.
 *
 * Child age and gender, and tobacco use, are legally sensitive (UAT UI-029,
 * MT-014): they are counted like any other field, so the number is right, and a
 * check that uses one is routed for the same manual review as the sensitive
 * targeting study type (`usesSensitiveProfile`, funnel.ts).
 */
export const PROFILE_FIELDS = [
    { key: 'hispanic', label: 'Hispanic or Latino', group: 'person' },
    { key: 'ethnicity', label: 'Ethnicity', group: 'person' },
    { key: 'language_preference', label: 'Language', group: 'person' },
    { key: 'education', label: 'Education', group: 'person' },
    { key: 'employment', label: 'Employment', group: 'person' },
    { key: 'income', label: 'Household income', group: 'household' },
    { key: 'rent_or_own', label: 'Rent or own', group: 'household' },
    { key: 'residence_type', label: 'Home type', group: 'household' },
    { key: 'occupant_count', label: 'People in the home', group: 'household' },
    { key: 'parent', label: 'Parent', group: 'household' },
    { key: 'parent_with_kids_under_18', label: 'Parent of kids under 18', group: 'household' },
    { key: 'children_count', label: 'Children under 18', group: 'household' },
    { key: 'adopted_child', label: 'Adopted child', group: 'household' },
    { key: 'vehicle_count', label: 'Vehicles', group: 'household' },
    { key: 'pet_count', label: 'Pets', group: 'household' },
    { key: 'pet_type', label: 'Pet type', group: 'household' },
    { key: 'cellular_carrier', label: 'Mobile carrier', group: 'devices' },
    { key: 'computer_ownership', label: 'Computer at home', group: 'devices' },
    { key: 'tablet', label: 'Devices owned', group: 'devices' },
    { key: 'msa', label: 'Metro area (MSA)', group: 'place' },
    { key: 'county', label: 'County', group: 'place' },
    { key: 'census_division', label: 'Census division', group: 'place' },
    { key: 'child_age_and_gender', label: 'Child age and gender', group: 'sensitive' },
    { key: 'tobacco_demo', label: 'Tobacco use (past 30 days)', group: 'sensitive' },
] as const;

export type ProfileFieldKey = (typeof PROFILE_FIELDS)[number]['key'];

export const PROFILE_FIELD_KEYS = PROFILE_FIELDS.map((f) => f.key) as ProfileFieldKey[];

export const PROFILE_GROUP_LABEL: Record<(typeof PROFILE_FIELDS)[number]['group'], string> = {
    person: 'About them',
    household: 'Household',
    devices: 'Devices',
    place: 'Where',
    sensitive: 'Needs approval before it is quoted',
};

/**
 * The fields bids ask for most, listed first in the form, and the ones they
 * almost never ask for, listed last (Bid Desk, UAT UI-039, 2026-10-14).
 */
export const COMMON_PROFILE_KEYS: ProfileFieldKey[] = [
    'hispanic',
    'ethnicity',
    'income',
    'parent_with_kids_under_18',
    'child_age_and_gender',
    'tobacco_demo',
    'education',
    'employment',
];
export const RARE_PROFILE_KEYS: ProfileFieldKey[] = [
    'occupant_count',
    'children_count',
    'adopted_child',
    'computer_ownership',
];

/** Fields whose targeting is legally sensitive: a check using one needs a manual review. */
export const SENSITIVE_PROFILE_KEYS: ProfileFieldKey[] = PROFILE_FIELDS.filter(
    (f) => f.group === 'sensitive',
).map((f) => f.key);

/** The sensitive fields a request targets, by label, in field order. */
export function usesSensitiveProfile(
    criteria: { kind: string; profile?: Record<string, string[]> }[],
): string[] {
    const used = new Set(
        criteria.flatMap((c) => (c.kind === 'demographic' ? Object.keys(c.profile ?? {}) : [])),
    );
    return SENSITIVE_PROFILE_KEYS.filter((k) => used.has(k)).map(profileFieldLabel);
}

export function profileFieldLabel(key: string): string {
    return PROFILE_FIELDS.find((f) => f.key === key)?.label ?? key.replace(/_/g, ' ');
}
